// src/pages/Register.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BrainCircuit,
  CheckCircle2,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  WalletCards,
} from "lucide-react";

import "../sass/pages/register.scss";

import Logo from "../assets/logoClaro.webp";

const PLANS = [
  {
    key: "essential",
    title: "PsyAssist Essencial",
    badge: "Ideal para começar",
    eyebrow: "Essencial",
    description:
      "Organize sua rotina profissional com os recursos fundamentais para agenda, pacientes e gestão do consultório.",
    path: "/plan-essential",
    icon: WalletCards,
    features: [
      "Agenda e organização da rotina",
      "Gestão de pacientes",
      "Base para evoluir sua operação",
    ],
  },
  {
    key: "premium",
    title: "PsyAssist Premium",
    badge: "Mais completo",
    eyebrow: "Premium",
    description:
      "Amplie sua operação com uma experiência mais completa e recursos inteligentes integrados ao PsyAssist.",
    path: "/plan-premium",
    icon: BrainCircuit,
    features: [
      "Tudo do plano Essencial",
      "Recursos Premium do sistema",
      "Experiência inteligente com PSYA",
    ],
    featured: true,
  },
];

const Register = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate("/login");
  };

  const handleSelectPlan = (path) => {
    navigate(path);
  };

  return (
    <main className="psy-register-page">
      <div
        className="psy-register-background"
        aria-hidden="true"
      >
        <span className="psy-register-background__orb psy-register-background__orb--one" />
        <span className="psy-register-background__orb psy-register-background__orb--two" />
        <span className="psy-register-background__grid" />
      </div>

      <div className="psy-register-layout">
        <aside className="psy-register-aside">
          <div className="psy-register-brand">
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Tecnologia para sua rotina profissional
              </span>
            </div>
          </div>

          <div className="psy-register-aside__content">
            <span className="psy-register-kicker">
              <Sparkles size={15} />
              Escolha seu workspace
            </span>

            <h1>
              Comece com a estrutura certa para sua prática.
            </h1>

            <p>
              Escolha o plano que melhor acompanha o momento atual do seu
              consultório. Você poderá revisar todos os detalhes antes de
              concluir a contratação.
            </p>

            <div className="psy-register-benefits">
              <div className="psy-register-benefit">
                <span>
                  <CheckCircle2 size={19} />
                </span>

                <div>
                  <strong>
                    Fluxo simples
                  </strong>

                  <small>
                    Escolha o plano, conclua seu cadastro e configure seu
                    workspace.
                  </small>
                </div>
              </div>

              <div className="psy-register-benefit">
                <span>
                  <BrainCircuit size={19} />
                </span>

                <div>
                  <strong>
                    Evolução com o PsyAssist
                  </strong>

                  <small>
                    Você poderá alterar o plano conforme sua operação crescer.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-register-security-card">
            <div>
              <LockKeyhole size={19} />
            </div>

            <p>
              <strong>
                Contratação transparente
              </strong>

              Nenhuma assinatura é concluída nesta etapa. Os detalhes do plano
              serão apresentados antes da confirmação.
            </p>
          </div>
        </aside>

        <section className="psy-register-panel">
          <div className="psy-register-panel__head">
            <div className="psy-register-panel__icon">
              <WalletCards
                size={22}
                aria-hidden="true"
              />
            </div>

            <div>
              <span>
                Escolha do plano
              </span>

              <h2>
                Conheça nossos planos
              </h2>

              <p>
                Selecione a opção que faz mais sentido para sua rotina
                profissional.
              </p>
            </div>
          </div>

          <div className="psy-register-progress">
            <div className="psy-register-progress__header">
              <span>
                Criação do workspace
              </span>

              <strong>
                Etapa 1 de 3
              </strong>
            </div>

            <div
              className="psy-register-progress__track"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow="33"
              aria-label="Progresso do cadastro"
            >
              <span />
            </div>
          </div>

          <div className="psy-register-plans">
            {PLANS.map((plan) => {
              const Icon = plan.icon;

              return (
                <button
                  key={plan.key}
                  type="button"
                  className={`psy-register-plan ${
                    plan.featured
                      ? "psy-register-plan--featured"
                      : ""
                  }`}
                  onClick={() =>
                    handleSelectPlan(plan.path)
                  }
                  aria-label={`Ver detalhes do plano ${plan.title}`}
                >
                  <div className="psy-register-plan__top">
                    <div className="psy-register-plan__icon">
                      <Icon
                        size={23}
                        aria-hidden="true"
                      />
                    </div>

                    <span className="psy-register-plan__badge">
                      {plan.badge}
                    </span>
                  </div>

                  <div className="psy-register-plan__content">
                    <span className="psy-register-plan__eyebrow">
                      {plan.eyebrow}
                    </span>

                    <strong>
                      {plan.title}
                    </strong>

                    <p>
                      {plan.description}
                    </p>

                    <ul>
                      {plan.features.map((feature) => (
                        <li key={feature}>
                          <BadgeCheck
                            size={16}
                            aria-hidden="true"
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="psy-register-plan__action">
                    <span>
                      Ver detalhes do plano
                    </span>

                    <ArrowRight
                      size={18}
                      aria-hidden="true"
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <section className="psy-register-note">
            <ShieldCheck
              size={18}
              aria-hidden="true"
            />

            <div>
              <strong>
                Você mantém o controle
              </strong>

              <p>
                O plano poderá ser alterado posteriormente conforme a evolução
                do consultório.
              </p>
            </div>
          </section>

          <button
            type="button"
            className="psy-register-back"
            onClick={handleGoBack}
          >
            <ArrowLeft
              size={17}
              aria-hidden="true"
            />
            Voltar para o login
          </button>
        </section>
      </div>
    </main>
  );
};

export default Register;
